/**
 * Utilidades para manejo de autenticación y sesiones
 */

// Verificar si la sesión ha expirado
export const isSessionExpired = () => {
  const sessionExpiration = localStorage.getItem('sessionExpiration');
  if (!sessionExpiration) return true;

  const expirationDate = new Date(sessionExpiration);
  const now = new Date();

  return now > expirationDate;
};

// Limpiar datos de sesión
export const clearSession = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  localStorage.removeItem('sessionExpiration');
  // Limpiar cualquier otro dato relacionado con la sesión
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('authState');
};

// Obtener el usuario actual de localStorage
export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;

    const user = JSON.parse(userStr);

    // Verificar si la sesión ha expirado
    if (isSessionExpired()) {
      clearSession();
      return null;
    }

    return user;
  } catch (error) {
    console.error('Error al obtener usuario actual:', error);
    clearSession();
    return null;
  }
};

// Validar formato de email
export const isValidEmail = email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validar fortaleza de contraseña robusta
export const validatePassword = password => {
  const errors = [];

  if (password.length < 8) {
    errors.push('La contraseña debe tener al menos 8 caracteres');
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('La contraseña debe contener al menos una letra mayúscula');
  }

  if (!/[a-z]/.test(password)) {
    errors.push('La contraseña debe contener al menos una letra minúscula');
  }

  if (!/\d/.test(password)) {
    errors.push('La contraseña debe contener al menos un número');
  }

  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    errors.push('La contraseña debe contener al menos un símbolo especial (!@#$%^&*)');
  }

  // Verificar patrones comunes débiles
  const commonPatterns = [
    /123456/,
    /qwerty/i,
    /password/i,
    /admin/i,
    /letmein/i,
    /welcome/i,
    /monkey/i,
    /dragon/i,
  ];

  const hasCommonPattern = commonPatterns.some(pattern => pattern.test(password));
  if (hasCommonPattern) {
    errors.push('La contraseña no debe contener patrones comunes (123456, qwerty, password, etc.)');
  }

  // Verificar que no sea solo repetición de caracteres
  if (/(.)\1{3,}/.test(password)) {
    errors.push('La contraseña no debe contener más de 3 caracteres repetidos consecutivos');
  }

  return errors;
};

// Calcular fuerza de contraseña (0-100)
export const calculatePasswordStrength = password => {
  if (!password) return 0;

  let score = 0;
  const checks = {
    length: password.length >= 8,
    hasLower: /[a-z]/.test(password),
    hasUpper: /[A-Z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSymbol: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    isLong: password.length >= 12,
    noCommon: !/123456|qwerty|password|admin/i.test(password),
    noRepeat: !/(.)\1{3,}/.test(password),
  };

  // Puntuación base
  if (checks.length) score += 20;
  if (checks.hasLower) score += 10;
  if (checks.hasUpper) score += 10;
  if (checks.hasNumber) score += 10;
  if (checks.hasSymbol) score += 15;

  // Bonificaciones
  if (checks.isLong) score += 15;
  if (checks.noCommon) score += 10;
  if (checks.noRepeat) score += 10;

  return Math.min(score, 100);
};

// Obtener mensaje de fuerza de contraseña
export const getPasswordStrengthMessage = password => {
  const strength = calculatePasswordStrength(password);

  if (strength < 30) {
    return { message: 'Muy débil', color: 'text-red-600', bgColor: 'bg-red-200' };
  } else if (strength < 50) {
    return { message: 'Débil', color: 'text-orange-600', bgColor: 'bg-orange-200' };
  } else if (strength < 70) {
    return { message: 'Moderada', color: 'text-yellow-600', bgColor: 'bg-yellow-200' };
  } else if (strength < 90) {
    return { message: 'Fuerte', color: 'text-blue-600', bgColor: 'bg-blue-200' };
  } else {
    return { message: 'Muy fuerte', color: 'text-green-600', bgColor: 'bg-green-200' };
  }
};

// Generar hash simple para localStorage (no para producción)
export const hashPassword = password => {
  // En producción esto debe manejarse en el backend
  return password;
};

// Configurar expiración de sesión
export const setSessionExpiration = (hours = 24) => {
  const expirationTime = new Date();
  expirationTime.setHours(expirationTime.getHours() + hours);
  localStorage.setItem('sessionExpiration', expirationTime.toISOString());
};

// Rate limiting para intentos de login
const LOGIN_ATTEMPTS_KEY = 'loginAttempts';
const LOGIN_BLOCK_TIME = 15 * 60 * 1000; // 15 minutos

export const getLoginAttempts = () => {
  try {
    const attemptsStr = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
    if (!attemptsStr) return { count: 0, lastAttempt: null };

    return JSON.parse(attemptsStr);
  } catch {
    return { count: 0, lastAttempt: null };
  }
};

export const incrementLoginAttempts = () => {
  const attempts = getLoginAttempts();
  const newAttempts = {
    count: attempts.count + 1,
    lastAttempt: new Date().toISOString(),
  };

  localStorage.setItem(LOGIN_ATTEMPTS_KEY, JSON.stringify(newAttempts));
  return newAttempts;
};

export const clearLoginAttempts = () => {
  localStorage.removeItem(LOGIN_ATTEMPTS_KEY);
};

export const isLoginBlocked = () => {
  const attempts = getLoginAttempts();

  if (attempts.count < 5) return false;

  if (!attempts.lastAttempt) return false;

  const lastAttemptTime = new Date(attempts.lastAttempt);
  const now = new Date();
  const timeDiff = now - lastAttemptTime;

  return timeDiff < LOGIN_BLOCK_TIME;
};

export const getBlockTimeRemaining = () => {
  const attempts = getLoginAttempts();

  if (!attempts.lastAttempt) return 0;

  const lastAttemptTime = new Date(attempts.lastAttempt);
  const now = new Date();
  const timeDiff = now - lastAttemptTime;
  const remaining = LOGIN_BLOCK_TIME - timeDiff;

  return Math.max(0, Math.ceil(remaining / 60000)); // En minutos
};
